import { deleteAPI, getAPI, postAPI, putAPI } from '@/store/api';
import { Action } from '@/store/api/actions';
import {
  AssignCorrectionFactor,
  AssignCrop,
  Block,
  BlocksWithFruitCounts,
  BlocksWithSummary,
  Crop,
  CropParameters,
  CropType,
  CropVariety,
  GeomBlock,
  PaginatedBlocks,
  SimpleBlock
} from '@/store/models/block';

export default new (class BlockAPI {
  public async getSimpleBlocks(query = ''): Promise<SimpleBlock[]> {
    return await getAPI<SimpleBlock[]>(Action.SimpleBlocks, query);
  }
  public async getAllMyBlocks(query = ''): Promise<PaginatedBlocks> {
    return await getAPI<PaginatedBlocks>(Action.Blocks, query);
  }
  public async getAllMyBlocksWithFruitCounts(
    query = ''
  ): Promise<BlocksWithFruitCounts[]> {
    return await getAPI<BlocksWithFruitCounts[]>(
      Action.BlocksWithFruitCounts,
      query
    );
  }
  public async getAllMyBlocksWithOnlyFruitCounts(
    query = ''
  ): Promise<BlocksWithFruitCounts[]> {
    return await getAPI<BlocksWithFruitCounts[]>(
      Action.BlocksWithOnlyFruitCounts,
      query
    );
  }
  public async getAllMyBlocksWithAvgDM(
    query = ''
  ): Promise<BlocksWithFruitCounts[]> {
    return await getAPI<BlocksWithFruitCounts[]>(Action.BlocksWithAvgDM, query);
  }
  public async getAllMyBlocksWithFloweringCount(
    query = ''
  ): Promise<BlocksWithFruitCounts[]> {
    return await getAPI<BlocksWithFruitCounts[]>(
      Action.BlocksWithFloweringCount,
      query
    );
  }
  public async getAllMyBlocksWithFruitSize(
    query = ''
  ): Promise<BlocksWithFruitCounts[]> {
    return await getAPI<BlocksWithFruitCounts[]>(
      Action.BlocksWithFruitSize,
      query
    );
  }
  public async getAllMyBlocksSummary(query = ''): Promise<BlocksWithSummary[]> {
    return await getAPI<BlocksWithSummary[]>(Action.BlockSummary, query);
  }
  public async getAllMyBlocksWithGeom(query = ''): Promise<GeomBlock[]> {
    return await getAPI<GeomBlock[]>(Action.GeomBlocks, query);
  }
  public async getBlockById(id: number): Promise<GeomBlock> {
    return await getAPI<GeomBlock>(`${Action.Blocks}/${id}`);
  }
  public async getMapFeatureBlockById(
    id: number,
    query = ''
  ): Promise<GeomBlock> {
    return await getAPI<GeomBlock>(
      `${Action.MapFeatureBlock.replace('{id}', String(id))}`,
      query
    );
  }

  public async importBlocks(data: FormData): Promise<unknown> {
    return await postAPI(Action.ImportBlocks, data);
  }

  public async exportFarmBlocks(farm: number): Promise<{ url: string }> {
    return await getAPI<{ url: string }>(
      Action.ExportFarmBlocks.replace('{farm_id}', String(farm))
    );
  }

  public async exportBlock(id: number): Promise<{ url: string }> {
    return await getAPI<{ url: string }>(
      Action.ExportBlock.replace('{block_id}', String(id))
    );
  }

  public async exportBlocks(query = ''): Promise<string[][]> {
    return await getAPI<string[][]>(Action.ExportBlocks, query);
  }

  public async createBlock(block: Block): Promise<GeomBlock> {
    return await postAPI<GeomBlock>(Action.Blocks, block);
  }

  public async updateBlock(block: Block): Promise<GeomBlock> {
    return await putAPI<GeomBlock>(`${Action.Blocks}/${block.id}`, block);
  }

  public async deleteBlock(id: number): Promise<void> {
    await deleteAPI(`${Action.Blocks}/${id}`);
  }

  public async getAllCrops(query = ''): Promise<Crop[]> {
    return await getAPI<Crop[]>(Action.Crops, query);
  }

  public async getCrop(id: number): Promise<Crop> {
    return await getAPI<Crop>(Action.CropDetail.replace('<id>', String(id)));
  }

  public async getCropParameters(): Promise<CropParameters> {
    return await getAPI<CropParameters>(Action.CropParameters);
  }

  public async createCrop(crop: Crop): Promise<Crop> {
    return await postAPI<Crop>(Action.Crops, crop);
  }

  public async updateCrop(crop: Crop): Promise<Crop> {
    return await putAPI<Crop>(`${Action.Crops}/${crop.id}`, crop);
  }

  public async deleteCrop(id: number): Promise<void> {
    await deleteAPI(`${Action.Crops}/${id}`);
  }

  public async getAllCropTypes(query = ''): Promise<CropType[]> {
    return await getAPI<CropType[]>(Action.CropTypes, query);
  }

  public async getAllCropVarieties(query = ''): Promise<CropVariety[]> {
    return await getAPI<CropVariety[]>(Action.CropVarieties, query);
  }

  public async assignCrop(
    assignCrop: AssignCrop
  ): Promise<{ message: string }> {
    return await putAPI<{ message: string }>(Action.AssignCrop, assignCrop);
  }

  public async assignCorrectionFactor(
    assignCorrectionFactor: AssignCorrectionFactor
  ): Promise<{ message: string }> {
    return await putAPI<{ message: string }>(
      Action.AssignCorrectionFactor,
      assignCorrectionFactor
    );
  }
  public async batchUploadPackHouse(data: FormData): Promise<{ id: number }> {
    return await postAPI(Action.PackHouseBatchUpload, data);
  }

  public async getCropType(id: number): Promise<CropType> {
    return await getAPI<CropType>(`${Action.CropTypes}/${id}`);
  }

  public async createCropType(cropType: CropType): Promise<CropType> {
    return await postAPI<CropType>(Action.CropTypes, cropType);
  }

  public async updateCropType(cropType: CropType): Promise<CropType> {
    return await putAPI<CropType>(
      `${Action.CropTypes}/${cropType.id}`,
      cropType
    );
  }

  public async deleteCropType(id: number): Promise<void> {
    await deleteAPI(`${Action.CropTypes}/${id}`);
  }

  public async getCropVariety(id: number): Promise<CropVariety> {
    return await getAPI<CropVariety>(`${Action.CropVarieties}/${id}`);
  }

  public async createCropVariety(cropVariety: CropVariety): Promise<CropVariety> {
    return await postAPI<CropVariety>(Action.CropVarieties, cropVariety);
  }

  public async updateCropVariety(cropVariety: CropVariety): Promise<CropVariety> {
    return await putAPI<CropVariety>(
      `${Action.CropVarieties}/${cropVariety.id}`,
      cropVariety
    );
  }

  public async deleteCropVariety(id: number): Promise<void> {
    await deleteAPI(`${Action.CropVarieties}/${id}`);
  }
})();
